import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { 
	Android as RobotIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon
} from "@material-ui/icons";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	Box,
	Typography,
	List,
	ListItem,
	ListItemText,
	Collapse
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	switchContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1.5),
		border: "1px solid " + theme.palette.divider,
		borderRadius: theme.shape.borderRadius,
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
		backgroundColor: theme.palette.background.default
	},
	infoBox: {
		backgroundColor: theme.palette.action.hover,
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
		border: "1px dashed " + theme.palette.divider,
	},
	listText: {
		fontSize: "0.85rem",
	},
	toggleButton: {
		display: "flex",
		alignItems: "center",
		cursor: "pointer",
		marginTop: 4,
		color: theme.palette.primary.main,
		"&:hover": {
			textDecoration: "underline",
		}
	}
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "¡Demasiado corto!")
		.max(50, "¡Demasiado largo!")
		.required("Este campo es obligatorio"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId, isFirstConnection }) => {
	const { user } = useContext(AuthContext);
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		farewellMessage: "",
		isDefault: false,
		autoChatbotEnabled: true
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showChatbotInfo, setShowChatbotInfo] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) {
				setWhatsApp(prev => ({ ...prev, isDefault: isFirstConnection }));
				return;
			};
			
			try {
				const { data } = await api.get("whatsapp/" + whatsAppId);
				setWhatsApp(data);

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId, isFirstConnection]);

	const handleSaveWhatsApp = async values => {
		const whatsappData = {
			...values,
			queueIds: selectedQueueIds,
			companyId: user.companyId
		};

		try {
			if (whatsAppId) {
				await api.put("/whatsapp/" + whatsAppId, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
		setShowChatbotInfo(false);
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: i18n.t("whatsappModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
										fullWidth
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="isDefault"
												checked={values.isDefault}
												disabled={isFirstConnection && !whatsAppId} 
											/>
										}
										label={i18n.t("whatsappModal.form.default")}
									/>
								</div>

								{/* SWITCH CHATBOT */}
								<div className={classes.switchContainer}>
									<Box display="flex" alignItems="center">
										<RobotIcon color={values.autoChatbotEnabled ? "primary" : "disabled"} style={{ marginRight: 8 }} />
										<Box>
											<Typography variant="body1" style={{ fontWeight: 500 }}>
												Respuesta Automática (Chatbot)
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{values.autoChatbotEnabled ? "Activo" : "Inactivo"}
											</Typography>
											
											<div 
												className={classes.toggleButton}
												onClick={() => setShowChatbotInfo(!showChatbotInfo)}
											>
												<Typography variant="caption" style={{ fontWeight: 600 }}>
													{showChatbotInfo ? "Ocultar detalles" : "Ver detalles"}
												</Typography>
												{showChatbotInfo ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
											</div>
										</Box>
									</Box>
									<FormControlLabel
										control={
											<Switch
												checked={values.autoChatbotEnabled}
												onChange={(e) => setFieldValue("autoChatbotEnabled", e.target.checked)}
												color="primary"
												name="autoChatbotEnabled"
											/>
										}
										label=""
									/>
								</div>

								<Collapse in={showChatbotInfo}>
									<div className={classes.infoBox}>
										<Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold" }}>
											¿Cómo funciona el Chatbot?
										</Typography>
										<Typography variant="body2" paragraph style={{ fontSize: "0.85rem" }}>
											Al activar esta opción, los nuevos mensajes serán atendidos automáticamente por el bot. El ticket <strong>no aparecerá como pendiente</strong> a menos que el paciente solicite hablar con recepción.
										</Typography>
										
										<Typography variant="subtitle2" style={{ fontSize: "0.85rem", fontWeight: "bold" }}>
											Menú de opciones automático:
										</Typography>
										<List dense disablePadding>
											<ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
												<ListItemText primary="1. Hablar con recepción (Deriva a agente)" classes={{ primary: classes.listText }} />
											</ListItem>
											<ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
												<ListItemText primary="2. Agendar cita (Enlace a web)" classes={{ primary: classes.listText }} />
											</ListItem>
											<ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
												<ListItemText primary="3. Reagendar o cancelar (Autogestión)" classes={{ primary: classes.listText }} />
											</ListItem>
											<ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
												<ListItemText primary="4. Ver mis citas (Autogestión)" classes={{ primary: classes.listText }} />
											</ListItem>
											<ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
												<ListItemText primary="5. Información de la clínica" classes={{ primary: classes.listText }} />
											</ListItem>
										</List>
									</div>
								</Collapse>
								
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.farewellMessage")}
										type="farewellMessage"
										multiline
										rows={5}
										fullWidth
										name="farewellMessage"
										error={
											touched.farewellMessage && Boolean(errors.farewellMessage)
										}
										helperText={
											touched.farewellMessage && errors.farewellMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={selectedIds => setSelectedQueueIds(selectedIds)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);